<script lang="ts" setup>
import { PositionCode } from '@rendora/common/entities/analysis_backend';
import Qrcode from '@rendora/common/features/contact/ui/Qrcode.vue';
import { getGlobalConfig } from '@rendora/common/utils/config';
import { jumpTo } from '@rendora/common/utils/jumpTo';
import { openCustomerService } from '@rendora/common/utils/kefu';
import { getFeUrl, getPlatformJumpLink } from '@rendora/common/utils/url';
import IconArrow from '@rendora/design/assets/icon-chevron.svg?component';

import IconCalendar from '@rendora/design/assets/calendar.svg?component';
import IconFloatCourse from '@rendora/design/assets/float-course-new.svg?component';
import IconFloatKefu from '@rendora/design/assets/float-kefu.svg?component';
import IconFloatMail from '@rendora/design/assets/float-mail.svg?component';
import IconFloatQA from '@rendora/design/assets/float-qa.svg?component';
import IconFloatSprit from '@rendora/design/assets/float-sprit.svg?component';
import IconFloatWX from '@rendora/design/assets/float-wx.svg?component';
import { Img } from '@rendora/design/components/img/index';
import { Text } from '@rendora/design/components/text/index';
import { useBreakpoint } from '@rendora/design/utils/responsive';
import { useLocalStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import { type Component, computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { showFeedback } from '../feedback';
import FloatPopover from './FloatPopover.vue';

// 类型定义
type ChildItem = {
  text?: string;
  handle?: () => void;
  class?: string;
  code?: PositionCode;
  link?: string;
  tip1?: string;
  tip2?: string;
  tip3?: string;
  source_from?: string;
};

type Item = {
  hover_name: string;
  text?: string;
  name: string;
  icon: Component;
  handle?: () => void;
  child?: ChildItem[];
  hasContent?: boolean;
};

// 配置和权限
const { isCN } = getGlobalConfig();
const { isMobile } = useBreakpoint();
// 响应式数据
const showPopTip = ref(false);
const showMenuAll = ref(true);
const activeItemName = ref('');
const showYanshiPopover = ref(false);
const lastVisitDate = useLocalStorage('youyan_mobile_float_daily_visit', '');
let autoCloseTimer: ReturnType<typeof setTimeout> | null = null;
const isMountedState = ref(false);

onMounted(() => {
  isMountedState.value = true;
});

onUnmounted(() => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
  }
});

watch(
  isMobile,
  val => {
    // SSR 环境下不执行
    if (import.meta.server) return;

    if (val) {
      const today = dayjs().format('YYYY-MM-DD');
      if (lastVisitDate.value !== today) {
        lastVisitDate.value = today;
        showMenuAll.value = true;
        setTimeout(() => {
          showYanshiPopover.value = true;
          activeItemName.value = 'yanshi';
          lockScroll();

          autoCloseTimer = setTimeout(() => {
            showYanshiPopover.value = false;
            activeItemName.value = '';
            unlockScroll();
            showMenuAll.value = false;
            autoCloseTimer = null;
          }, 3000);
        }, 500);
      }
    }
  },
  { immediate: true },
);

// 工具函数
const getQrcodeURI = (type: 'b' | 'c', name: string) => {
  return getFeUrl(`/youyan-platform/qrcode/${type}/${name}.png`);
};

// 锁定和解锁页面滚动
const lockScroll = () => {
  // SSR 环境下不执行
  if (import.meta.server || typeof document === 'undefined') return;

  document.body.style.overflow = 'hidden';
  document.body.style.touchAction = 'none';
};

const unlockScroll = () => {
  // SSR 环境下不执行
  if (import.meta.server || typeof document === 'undefined') return;

  document.body.style.overflow = '';
  document.body.style.touchAction = '';
};

// 事件处理器
function onClick(item: Item | ChildItem) {
  if ('handle' in item && item.handle) {
    item.handle();
  }
}

const onOpenChange = (visible: boolean, item: Item) => {
  if (item.name === 'yanshi') {
    showYanshiPopover.value = visible;
    if (!visible && autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
  }

  // 可以在这里添加其他逻辑
  if (isMobile.value) {
    if (visible) {
      lockScroll();
      activeItemName.value = item.name;
    } else {
      unlockScroll();
      if (activeItemName.value === item.name) {
        activeItemName.value = '';
      }
    }
  }
};

// 移动端 Popover 定位类名（仅移动端生效）
const getMobileContentClass = (itemName: string) => {
  if (!isMobile.value) return '';
  return itemName !== 'yanshi'
    ? 'float-popover-mobile-bottom'
    : 'float-popover-mobile-single-bottom';
};

// 引流图标配置
const DIVERSION_MENU = computed<Item[]>(() => [
  {
    hover_name: '咨询', // @xi18n-ignore
    name: 'wechat',
    icon: IconFloatWX,
    child: [
      {
        code: PositionCode['侧边栏-微信咨询-企业咨询'], // @xi18n-ignore
        tip1: '企业咨询', // @xi18n-ignore
        tip2: '扫码加产品专家·速响应', // @xi18n-ignore
        show: true,
        class: 'is-padded',
        source_from: 'youyan',
      },
      {
        link: getQrcodeURI('c', '14'),
        tip1: '个人咨询', // @xi18n-ignore
        tip2: '扫码领取新手必学课', // @xi18n-ignore
        show: true,
        class: 'is-padded',
        source_from: 'youyan',
        tip3: '长按或截图保存二维码',
      },
    ],
  },
  {
    hover_name: '预约', // @xi18n-ignore
    name: 'yanshi',
    icon: IconCalendar,
    child: [
      {
        code: PositionCode['侧边栏-预约产品演示'], // @xi18n-ignore
        tip1: '预约产品功能演示', // @xi18n-ignore
        tip2: '扫码领有言应用知识图谱', // @xi18n-ignore
        show: true,
        class: 'is-padded',
        source_from: 'youyan',
        tip3: '长按或截图保存二维码',
      },
    ],
  },
  {
    hover_name: '干货', // @xi18n-ignore
    name: 'crate',
    icon: IconFloatSprit,
    child: [
      {
        link: getQrcodeURI('c', '8'),
        tip1: '新手速成实战营', // @xi18n-ignore
        tip2: '扫码立即入营', // @xi18n-ignore
        show: true,
        source_from: 'youyan',
      },
      {
        code: PositionCode['侧边栏-AI干货-行业趋势报告'], // @xi18n-ignore
        tip1: '行业趋势报告&实操直播课', // @xi18n-ignore
        tip2: '扫码立即领取', // @xi18n-ignore
        class: 'is-padded',
        show: true,
        source_from: 'youyan',
        tip3: '长按或截图保存二维码',
      },
    ],
  },
  {
    hover_name: '客服', // @xi18n-ignore
    name: 'customer',
    icon: IconFloatKefu,
    handle: () => openCustomerService(),
    hasContent: false,
  },
]);

// 服务内容配置
const SERVICE_CONTENT = computed<Item[]>(() => [
  {
    hover_name: '帮助中心', // @xi18n-ignore
    text: '产品更新日志可以在此处查看', // @xi18n-ignore
    name: 'help',
    icon: IconFloatQA,
    hasContent: false,
    handle: () => {
      jumpTo(getPlatformJumpLink('/help-center'));
    },
  },
  {
    hover_name: '操作教程', // @xi18n-ignore
    name: 'course',
    icon: IconFloatCourse,
    handle: () => jumpTo(getPlatformJumpLink('/tutorial')),
    hasContent: false,
  },
  {
    hover_name: '意见反馈', // @xi18n-ignore
    name: 'email',
    icon: IconFloatMail,
    handle: () => showFeedback(),
    hasContent: false,
  },
]);
</script>
<template>
  <div class="float-features" :class="{ 'is-rendora': !isCN }">
    <div v-if="isCN && DIVERSION_MENU.length" class="float-features__container">
      <div class="float-features__service">
        <!-- 引流图标 -->
        <div class="float-features__menu">
          <Text v-if="!showMenuAll" variant="12" color="t1" class="float-features__title">
            {{ $t('zi-xun-bang-zhu') }}
          </Text>
          <div class="float-features__main" :class="{ 'is-expanded': showMenuAll }">
            <div v-for="item in DIVERSION_MENU" :key="item.name" class="float-features__item">
              <FloatPopover
                v-if="item.name !== 'customer'"
                placement="left"
                :trigger="isMobile ? 'click' : 'hover'"
                :content-class="getMobileContentClass(item.name)"
                :show-arrow="!isMobile"
                :offset="20"
                :open="item.name === 'yanshi' ? showYanshiPopover : undefined"
                @openChange="(visible: boolean) => onOpenChange(visible, item)"
              >
                <template #content>
                  <div class="float-features__popover-content">
                    <div
                      v-for="(detail, index) in item.child?.filter(
                        (childItem: any) => childItem.show,
                      )"
                      :key="index"
                      class="float-features__detail"
                    >
                      <Text
                        variant="14"
                        color="t1"
                        weight="medium"
                        class="float-features__detail-title"
                      >
                        {{ detail?.tip1 }}
                      </Text>
                      <div class="float-features__detail-image" :class="detail?.class">
                        <Qrcode
                          v-if="detail.code"
                          :code="detail.code"
                          source_from="youyan"
                          class="float-features__qr-code"
                        />
                        <Img
                          v-else-if="detail?.link"
                          class="float-features__qr-code"
                          :src="detail.link"
                        />
                      </div>
                      <Text variant="14" color="t3" class="float-features__detail-description">
                        {{ detail?.tip2 }}
                      </Text>
                      <Text
                        v-if="detail?.tip3"
                        variant="12"
                        color="t4"
                        class="float-features__detail-description-for-mobile"
                      >
                        {{ detail?.tip3 }}
                      </Text>
                    </div>
                  </div>
                </template>
                <div
                  class="float-features__icon-wrapper"
                  :class="{ 'is-active': activeItemName === item.name }"
                >
                  <component :is="item.icon" />
                  <Text variant="12" color="t3" class="float-features__icon-label">
                    {{ item.hover_name }}
                  </Text>
                </div>
              </FloatPopover>
              <div
                v-else
                class="float-features__icon-wrapper"
                :class="{ 'is-active': activeItemName === item.name }"
                @click="onClick(item)"
              >
                <component :is="item.icon" />
                <Text variant="12" color="t3" class="float-features__icon-label">
                  {{ item.hover_name }}
                </Text>
              </div>
            </div>
            <template v-if="isMountedState">
              <div v-if="showMenuAll && !isMobile" class="float-features__divider" />
              <template v-if="!isMobile" v-for="item in SERVICE_CONTENT" :key="item.name">
                <FloatPopover v-if="!showPopTip ? true : item.name !== 'help'" placement="left">
                  <template #content>
                    <template v-if="item.child">
                      <div
                        v-for="detail in item.child.filter((childItem: any) => childItem.show)"
                        :key="detail.text"
                        class="float-features__child-item"
                        @click="onClick(detail)"
                      >
                        <Text variant="14" color="t3">{{ detail.text }}</Text>
                      </div>
                    </template>
                    <template v-else-if="item.name !== 'code' && !item.child">
                      <Text variant="14" color="t3" class="float-features__tooltip">
                        {{ item.hover_name }}
                      </Text>
                    </template>
                  </template>
                  <div class="float-features__icon-wrapper no-title" @click="onClick(item)">
                    <component :is="item.icon" />
                  </div>
                </FloatPopover>
                <FloatPopover
                  v-else-if="item.name === 'help' && showPopTip"
                  placement="left"
                  :open="true"
                >
                  <template #content>
                    <Text variant="14" color="t3" class="float-features__tooltip">
                      {{ item.hover_name }}
                    </Text>
                  </template>
                  <div class="float-features__icon-wrapper no-title" @click="onClick(item)">
                    <component :is="item.icon" />
                  </div>
                </FloatPopover>
              </template>
            </template>
          </div>
          <div class="float-features__toggle" @click="showMenuAll = !showMenuAll">
            <IconArrow
              class="float-features__arrow"
              :class="{
                'is-active': showMenuAll,
                'is-inactive': !showMenuAll,
              }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

// ============================================================================
// 设计令牌 (Design Tokens)
// ============================================================================
$float-menu-width: 48px;
$qr-code-size: 148px;
$qr-code-size-mobile: 148px;
$expand-transition-duration: 0.6s;
$expand-max-height: 450px;

// ============================================================================
// 根容器
// ============================================================================
.float-features {
  position: fixed;
  right: 10px;
  bottom: 78px;
  z-index: var(--x-layer-absolute);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @include mobile {
    right: responsive-size(10px);
    bottom: responsive-size(68px);
  }
}

// 非 CN 区域样式
.float-features.is-rendora {
  right: 30px;
  bottom: 100px;

  @include mobile {
    right: responsive-size(10px);
    bottom: responsive-size(68px);
  }
}

// ============================================================================
// 容器与服务区域
// ============================================================================
.float-features__container {
  margin-top: 12px;
}

.float-features__service {
  display: flex;
  flex-direction: column;
  align-items: center;
}

// ============================================================================
// 菜单面板
// ============================================================================
.float-features__menu {
  width: $float-menu-width;
  display: flex;
  flex-direction: column;
  z-index: 20;
  overflow-y: hidden;
  border-radius: var(--x-radius-medium);
  outline: 1px solid var(--x-back-5);
  background-color: var(--x-back-1);
  box-shadow: var(--x-shadow);
  padding-bottom: 12px;

  @include mobile {
    width: responsive-size($float-menu-width);
    outline: unset;
    border: responsive-size(1px) solid var(--x-back-5);
    padding-bottom: responsive-size(12px);
  }
}

.float-features__title {
  padding: 12px 12px 6px;
  margin-top: 2px;

  @include mobile {
    padding: responsive-size(10px) responsive-size(10px) responsive-size(4px);
    text-align: center;
  }
}

// ============================================================================
// 主内容区 (可展开)
// ============================================================================
.float-features__main {
  max-height: 0;
  visibility: hidden;
  transform-origin: 50% 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @include mobile {
    gap: 0;
  }
}

.float-features__main.is-expanded {
  visibility: visible;
  transition: max-height $expand-transition-duration;
  max-height: $expand-max-height;
  padding: 12px 0;

  @include mobile {
    max-height: none;
    padding: responsive-size(2px);
  }
}

// ============================================================================
// 菜单项
// ============================================================================
.float-features__item {
  display: flex;
  flex-direction: column;
}

// ============================================================================
// 图标容器
// ============================================================================
.float-features__icon-wrapper {
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans',
    sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 100%;
  padding: 0;
  text-align: center;
  line-height: 1.4;
  font-size: 24px;
  color: var(--x-text-1);
  svg {
    font-size: 24px;
  }

  @include mobile {
    font-size: var(--x-font-size-12);
    width: responsive-size(40px);
    padding: responsive-size(7px);
    border-radius: var(--x-radius-medium);

    .svg-icon {
      font-size: responsive-size(24px);
    }

    &:active {
      background-color: var(--x-back-4);
    }
  }
}

.float-features__icon-wrapper.is-active {
  @include mobile {
    background-color: var(--x-back-4);
  }
}

.float-features__icon-wrapper.no-title {
  height: 24px;
}

.float-features__icon-label {
  padding: 0 4px;
  margin-top: 3px;

  @include mobile {
    margin-top: responsive-size(2px);
    white-space: nowrap;
    padding: 0;
  }
}

// ============================================================================
// 切换按钮与箭头
// ============================================================================
.float-features__toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 100%;
}

.float-features__arrow {
  font-size: 16px;
  transition: transform 0.3s;
  @include mobile {
    font-size: responsive-size(16px);
  }
  &.is-active {
    transform: rotate(0deg);
  }
  &.is-inactive {
    transform: rotate(180deg);
  }
}

// ============================================================================
// 分割线
// ============================================================================
.float-features__divider {
  width: 100%;
  height: 1px;
  background: var(--x-back-5);
}

// ============================================================================
// Popover 内容
// ============================================================================
.float-features__popover-content {
  padding: 16px;
  display: flex;
  gap: 16px;
  cursor: pointer;

  @include mobile {
    flex-direction: column;
    align-items: center;
    padding: 0;
    gap: 0;
  }
}

// ============================================================================
// 详情卡片 (二维码展示)
// ============================================================================
.float-features__detail {
  display: flex;
  flex-direction: column;
  align-items: center;

  @include mobile {
    &:not(:first-child)::before {
      content: '';
      width: 140px;
      height: 1px;
      background: var(--x-back-4);
      margin: responsive-size(12px) 0;
    }
  }
}

.float-features__detail-title {
  margin-bottom: 8px;
  text-align: center;

  @include mobile {
    margin-bottom: responsive-size(6px);
    font-size: var(--x-font-size-12);
    line-height: var(--x-text-12-line-height);
  }
}

.float-features__detail-image {
  width: $qr-code-size;
  height: $qr-code-size;

  @include mobile {
    width: responsive-size($qr-code-size-mobile);
    height: responsive-size($qr-code-size-mobile);
  }
}

// ============================================================================
// 二维码
// ============================================================================
.float-features__qr-code {
  width: 100%;
  height: 100%;
}

.float-features__qr-code.is-padded {
  padding: 6px;
}

// ============================================================================
// 描述文本
// ============================================================================
.float-features__detail-description {
  margin-top: 10px;
  text-align: center;

  @include mobile {
    margin-top: responsive-size(6px);
    font-size: var(--x-text-10-size);
    line-height: var(--x-text-10-line-height);
  }
}

.float-features__detail-description-for-mobile {
  display: none;

  @include mobile {
    display: block;
    margin-top: responsive-size(12px);
    font-size: var(--x-text-10-size);
    line-height: var(--x-text-10-line-height);
  }
}

// ============================================================================
// 子项 (文本链接)
// ============================================================================
.float-features__child-item {
  cursor: pointer;
  padding: 5px 0;

  &:hover {
    color: var(--text-primary);
  }
}

// ============================================================================
// Tooltip 文本
// ============================================================================
.float-features__tooltip {
  display: block;
  padding: 8px 12px;
  white-space: nowrap;
}
</style>

<!-- 全局样式：移动端 Popover 底部定位 -->
<style lang="scss">
@use '@rendora/design/styles/mixins.scss' as *;

.float-popover-mobile-bottom {
  @include mobile {
    position: fixed;
    // 使用 CSS 定位
    bottom: responsive-size(10px);
    right: responsive-size(68px);
  }
}
.float-popover-mobile-single-bottom {
  @include mobile {
    position: fixed;
    // 使用 CSS 定位
    bottom: responsive-size(128px);
    right: responsive-size(68px);
  }
}
</style>
